<template>
  <Modal v-model="dialogVisible" class="vbudget-modal">
    <template #content>
      <div class="modal-header">
        <h5 class="modal-title">
          {{ mode === 'edit' ? $t('billing.vbudgets.edit_vbudget') : $t('billing.vbudgets.create_vbudget') }}
        </h5>
        <button type="button" class="btn-close" @click="dialogVisible = false"></button>
      </div>
      <div class="modal-body">
        <div v-if="parentNode" class="parent-context mb-3 p-3 rounded">
          <small class="text-muted d-block mb-1">
            {{ mode === 'edit' ? $t('billing.vbudgets.belongs_to') : $t('billing.vbudgets.creating_under') }}
          </small>
          <div class="d-flex align-items-center gap-2">
            <IconPigMoney v-if="parentNode.type === 'customer_budget'" size="16" class="text-primary" />
            <IconWallet v-else size="16" class="text-primary" />
            <strong>{{ parentName }}</strong>
          </div>
          <small class="text-muted mt-1 d-block">
            {{ $t('billing.vbudgets.parent_available') }}:
            {{ formatCurrency(parentNode.availability.available_unallocated, currency) }}
          </small>
        </div>

        <form @submit.prevent>
          <LeadInput
            v-model="form.name"
            :label="$t('billing.vbudgets.form.name')"
            is-required
            :placeholder="$t('billing.vbudgets.form.name_placeholder')"
            :errors="errors.name"
            class="mb-3"
            @input="clearError('name')" />

          <div class="mb-3">
            <label class="form-label">{{ $t('billing.vbudgets.form.description') }}</label>
            <textarea
              class="form-control"
              v-model="form.description"
              :placeholder="$t('billing.vbudgets.form.description_placeholder')"
              rows="2"></textarea>
            <FormErrorList v-if="errors.description" :errors="errors.description" class="mt-1" />
          </div>

          <ChannelReferenceSelector
            v-model="form.reference_channels"
            :label="$t('billing.vbudgets.form.reference_channels')"
            class="mb-1" />
          <small class="text-muted d-block mb-3">{{ $t('billing.vbudgets.auto_selection_description') }}</small>

          <LiFormInput
            v-memo="[form.allocation]"
            v-model.number="form.allocation"
            type="currency"
            :label="$t('billing.vbudgets.form.allocation')"
            is-required
            :currency="currency"
            :locale="defaultLocale"
            :placeholder="$t('billing.vbudgets.form.allocation_placeholder')"
            :errors="errors.allocation"
            class="mb-3"
            @input="onAllocationInput" />

          <div class="mb-3">
            <div class="form-check form-switch">
              <input class="form-check-input" type="checkbox" id="limitAvailability" v-model="limitAvailability" />
              <label class="form-check-label" for="limitAvailability">
                {{ $t('billing.vbudgets.form.limit_availability') }}
              </label>
            </div>
          </div>

          <div v-if="limitAvailability" class="mb-3">
            <label class="form-label">{{ $t('billing.vbudgets.form.date_range') }}</label>
            <Datepicker
              v-model="dateRange"
              range
              :enable-time-picker="false"
              :formats="datePickerFormats"
              :locale="datepickerLocale"
              text-input
              auto-apply
              :placeholder="$t('billing.vbudgets.form.select_date_range')" />
            <FormErrorList v-if="errors.date_range" :errors="errors.date_range" class="mt-1" />
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <GenericButton class="btn-white" :icon="IconX" @click="dialogVisible = false">
          {{ $t('cancel') }}
        </GenericButton>
        <GenericButton class="btn-primary" :icon="IconCheck" @click="$emit('save')" :disabled="!isFormValid">
          {{ $t('btn.save') }}
        </GenericButton>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Modal, FormInput as LiFormInput, FormErrorList } from '@prospective/lithium';
import { VueDatePicker as Datepicker } from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import { IconPigMoney, IconWallet, IconX, IconCheck } from '@tabler/icons-vue';
import { formatCurrency } from '@/locale/format';
import { defaultLocale } from '@/config/app';
import GenericButton from '@/components/generic/GenericButton.vue';
import LeadInput from '@/components/form/LeadInput.vue';
import ChannelReferenceSelector from '@/components/form/ChannelReferenceSelector.vue';
import type { IBudgetTreeNode } from '@/types/budget';
import { getBudgetNodeName } from '@/helpers/budgetUtils';
import { getDatepickerLocale } from '@/locale/datepickerLocale';
import translator from '@/locale/translator';

const props = defineProps<{
  modelValue: boolean;
  mode: 'create' | 'edit';
  parentNode: IBudgetTreeNode | null;
  dateRange: [Date, Date] | null;
  limitAvailability: boolean;
  currency: string;
  errors: Record<string, string[]>;
}>();

const form = defineModel<{
  id?: number;
  name: string;
  description: string;
  allocation: number;
  allocation_type: 'fixed' | 'percentage';
  reference_channels: string[];
}>('form', { required: true });

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'update:dateRange', value: [Date, Date] | null): void;
  (e: 'update:limitAvailability', value: boolean): void;
  (e: 'save'): void;
  (e: 'clearError', field: string): void;
}>();

const allocationHasValue = ref(form.value.allocation > 0);

watch(
  () => form.value.allocation,
  (val) => {
    allocationHasValue.value = val > 0;
  }
);

// just check if there's a non-zero digit because the v-model only updates on blur for the currency input
function onAllocationInput(e: Event) {
  allocationHasValue.value = /[1-9]/.test((e.target as HTMLInputElement).value);
  if (allocationHasValue.value) emit('clearError', 'allocation');
}

const isFormValid = computed(() => !!(form.value.name && allocationHasValue.value));

const datePickerFormats = computed(() => ({
  input: translator.get('date.format') || 'yyyy-MM-dd',
}));

const datepickerLocale = computed(() => getDatepickerLocale());

const dialogVisible = computed({
  get: () => props.modelValue,
  set: (val: boolean) => emit('update:modelValue', val),
});

const dateRange = computed({
  get: () => props.dateRange,
  set: (val: [Date, Date] | null) => emit('update:dateRange', val),
});

const limitAvailability = computed({
  get: () => props.limitAvailability,
  set: (val: boolean) => emit('update:limitAvailability', val),
});

watch(limitAvailability, (val) => {
  if (!val) dateRange.value = null;
});

const parentName = computed(() => {
  if (!props.parentNode) return '';
  return getBudgetNodeName(props.parentNode);
});

function clearError(field: string) {
  if (props.errors[field] && form.value.name.length <= 255) {
    emit('clearError', field);
  }
}
</script>

<style scoped>
:deep(.vbudget-modal) {
  max-width: 480px;
}

.parent-context {
  background-color: var(--tblr-light, #f7f7f7);
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.modal-footer {
  background: #f8f8f8;
  padding: 12px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
